import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Plus, Mail, Trash2, RotateCcw, X } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Button } from '@/components/ui/Button';
import { FadeIn } from '@/components/animations/FadeIn';
import { EmptyState } from '@/components/emptyState/EmptyState';
import type { WorkspaceMembersPageQuery as WorkspaceMembersPageQueryType } from '@/__generated__/WorkspaceMembersPageQuery.graphql';
import type { WorkspaceMembersPageRevokeMutation as RevokeType } from '@/__generated__/WorkspaceMembersPageRevokeMutation.graphql';
import type { WorkspaceMembersPageResendMutation as ResendType } from '@/__generated__/WorkspaceMembersPageResendMutation.graphql';
import type { WorkspaceMembersPageRemoveMutation as RemoveType } from '@/__generated__/WorkspaceMembersPageRemoveMutation.graphql';
import type { WorkspaceMembersPageRemoveInviteeMutation as RemoveInviteeType } from '@/__generated__/WorkspaceMembersPageRemoveInviteeMutation.graphql';
import type { WorkspaceMembersPageInviteMutation as InviteType } from '@/__generated__/WorkspaceMembersPageInviteMutation.graphql';

const inviteFormSchema = z.object({
  email: z.string().email('A valid email is required'),
});
type InviteFormValues = z.infer<typeof inviteFormSchema>;

const pageQuery = graphql`
  query WorkspaceMembersPageQuery($workspaceId: ID!) {
    workspace(id: $workspaceId) {
      id
      name
      viewerMembership { role }
      members {
        id
        role
        joinedAt
        user {
          id
          name
          email
        }
      }
      pendingInvites {
        id
        email
        role
        status
        createdAt
        expiresAt
      }
      acceptedInvites {
        id
        email
        role
        status
        acceptedAt
      }
    }
  }
`;

const inviteMutation = graphql`
  mutation WorkspaceMembersPageInviteMutation(
    $workspaceId: ID!
    $email: String!
    $role: WorkspaceRole
  ) {
    createWorkspaceInvite(workspaceId: $workspaceId, email: $email, role: $role) {
      id
      email
      status
      expiresAt
    }
  }
`;

const revokeMutation = graphql`
  mutation WorkspaceMembersPageRevokeMutation($inviteId: ID!) {
    revokeWorkspaceInvite(inviteId: $inviteId)
  }
`;

const resendMutation = graphql`
  mutation WorkspaceMembersPageResendMutation($inviteId: ID!) {
    resendWorkspaceInvite(inviteId: $inviteId) {
      id
      expiresAt
    }
  }
`;

const removeMutation = graphql`
  mutation WorkspaceMembersPageRemoveMutation($memberId: ID!) {
    removeWorkspaceMember(memberId: $memberId)
  }
`;

const removeInviteeMutation = graphql`
  mutation WorkspaceMembersPageRemoveInviteeMutation($inviteId: ID!) {
    removeWorkspaceInvitee(inviteId: $inviteId)
  }
`;

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  MEMBER: 'Member',
};

function initialsFor(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('') || '?';
}

export function WorkspaceMembersPage() {
  const { id: routeWorkspaceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeWorkspaceId, loading } = useWorkspace();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const onManagePermissionChange = useCallback((next: boolean) => {
    setCanManage(next);
  }, []);

  useEffect(() => {
    if (
      !loading &&
      activeWorkspaceId &&
      routeWorkspaceId !== activeWorkspaceId
    ) {
      navigate('/workspace/' + activeWorkspaceId + '/settings/members', {
        replace: true,
      });
    }
  }, [activeWorkspaceId, loading, navigate, routeWorkspaceId]);

  if (loading || !activeWorkspaceId || routeWorkspaceId !== activeWorkspaceId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading members...
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <FadeIn>
        <div className="flex flex-col gap-4 px-6 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Members</h1>
            <p className="text-sm text-muted-foreground">
              Invite teammates to this workspace.
            </p>
          </div>
          <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto">
            <Button
              onClick={() => navigate('/daysheetly')}
              variant="outline"
              size="sm"
              data-testid="members-back"
              className="w-full sm:w-auto"
            >
              Back to Day Sheetly
            </Button>
            {canManage ? (
              <Button
                onClick={() => setIsInviteOpen(true)}
                size="sm"
                data-testid="members-invite-open"
                className="w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" />
                Invite member
              </Button>
            ) : null}
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.05} className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
        <WorkspaceMembersPageContent
          workspaceId={activeWorkspaceId}
          isInviteOpen={isInviteOpen}
          onInviteClose={() => setIsInviteOpen(false)}
          onManagePermissionChange={onManagePermissionChange}
        />
      </FadeIn>
    </div>
  );
}

function WorkspaceMembersPageContent({
  workspaceId,
  isInviteOpen,
  onInviteClose,
  onManagePermissionChange,
}: {
  workspaceId: string;
  isInviteOpen: boolean;
  onInviteClose: () => void;
  onManagePermissionChange: (canManage: boolean) => void;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const data = useLazyLoadQuery<WorkspaceMembersPageQueryType>(pageQuery, {
    workspaceId,
  }, { fetchPolicy: 'network-only', fetchKey: refreshKey });
  const [commitInvite, isInviting] = useMutation<InviteType>(inviteMutation);
  const [commitRevoke, isRevoking] = useMutation<RevokeType>(revokeMutation);
  const [commitResend, isResending] = useMutation<ResendType>(resendMutation);
  const [commitRemove, isRemoving] = useMutation<RemoveType>(removeMutation);
  const [commitRemoveInvitee, isRemovingInvitee] =
    useMutation<RemoveInviteeType>(removeInviteeMutation);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const ws = data.workspace;
  const viewerRole = ws?.viewerMembership?.role ?? null;
  const canManage = viewerRole === 'OWNER' || viewerRole === 'ADMIN';
  const acceptedInvites = ws?.acceptedInvites ?? [];

  useEffect(() => {
    onManagePermissionChange(canManage);
  }, [canManage, onManagePermissionChange]);

  if (!ws) {
    return (
      <EmptyState
        title="Workspace not found"
        description="You don't have access to this workspace."
      />
    );
  }

  const refresh = () => setRefreshKey((current) => current + 1);

  const onInvite = (values: InviteFormValues) => {
    commitInvite({
      variables: { workspaceId, email: values.email.trim().toLowerCase() },
      onCompleted: (response, errors) => {
        if (errors?.[0]) {
          toast.error(errors[0].message);
          return;
        }
        toast.success(`Invitation sent to ${values.email}`);
        onInviteClose();
        refresh();
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });
  };

  return (
    <>
      <div className="overflow-x-auto rounded-md border bg-background">
        <table className="w-full min-w-[34rem] text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr className="border-b">
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3 text-right" />
            </tr>
          </thead>
          <tbody data-testid="members-table">
            {ws.members.map((m) => (
              <tr key={m.id} className="border-b last:border-0">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sidebar-active text-xs font-bold text-white">
                      {initialsFor(m.user.name)}
                    </div>
                    <div>
                      <div className="font-medium">{m.user.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {m.user.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {ROLE_LABELS[m.role] ?? m.role}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {new Date(m.joinedAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage && m.role !== 'OWNER' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`members-remove-${m.id}`}
                      aria-label={`Remove ${m.user.name} from workspace`}
                      title={`Remove ${m.user.name} from workspace`}
                      disabled={isRemoving}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${m.user.name} from this workspace?`,
                          )
                        ) {
                          const actionId = `remove:${m.id}`;
                          setActiveAction(actionId);
                          commitRemove({
                            variables: { memberId: m.id },
                            onCompleted: (response, errors) => {
                              setActiveAction(null);
                              if (errors?.[0]) {
                                toast.error(errors[0].message);
                                return;
                              }
                              if (!response.removeWorkspaceMember) {
                                toast.error('Member could not be removed');
                                return;
                              }
                              toast.success(`${m.user.name} removed`);
                              refresh();
                            },
                            onError: (err) => {
                              setActiveAction(null);
                              toast.error(err.message);
                            },
                          });
                        }
                      }}
                    >
                      <Trash2
                        className={`mr-2 h-4 w-4 text-destructive ${activeAction === `remove:${m.id}` ? 'animate-pulse' : ''}`}
                      />
                      {activeAction === `remove:${m.id}` ? 'Removing…' : 'Remove'}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {acceptedInvites.map((inv) => (
              <tr key={inv.id} className="border-b last:border-0" data-testid={`accepted-invite-${inv.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                      ?
                    </div>
                    <div>
                      <div className="font-medium">{inv.email}</div>
                      <div className="text-xs text-muted-foreground">
                        Member — account not created yet
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {ROLE_LABELS[inv.role] ?? inv.role}
                </td>
                <td className="px-4 py-3 text-muted-foreground">
                  {inv.acceptedAt
                    ? new Date(inv.acceptedAt).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-4 py-3 text-right">
                  {canManage ? (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`accepted-remove-${inv.id}`}
                      aria-label={`Remove ${inv.email} from workspace`}
                      title={`Remove ${inv.email} from workspace`}
                      disabled={isRemovingInvitee}
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Remove ${inv.email} from this workspace?`,
                          )
                        ) {
                          return;
                        }

                        const actionId = `remove-invitee:${inv.id}`;
                        setActiveAction(actionId);
                        commitRemoveInvitee({
                          variables: { inviteId: inv.id },
                          onCompleted: (response, errors) => {
                            setActiveAction(null);
                            if (errors?.[0]) {
                              toast.error(errors[0].message);
                              return;
                            }
                            if (!response.removeWorkspaceInvitee) {
                              toast.error('Member could not be removed');
                              return;
                            }
                            toast.success(`${inv.email} removed`);
                            refresh();
                          },
                          onError: (err) => {
                            setActiveAction(null);
                            toast.error(err.message);
                          },
                        });
                      }}
                    >
                      <Trash2
                        className={`mr-2 h-4 w-4 text-destructive ${activeAction === `remove-invitee:${inv.id}` ? 'animate-pulse' : ''}`}
                      />
                      {activeAction === `remove-invitee:${inv.id}`
                        ? 'Removing…'
                        : 'Remove'}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Awaiting account
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Pending invites
      </h2>
      <div
        className="mt-2 overflow-x-auto rounded-md border bg-background"
        data-testid="invites-table"
      >
        {ws.pendingInvites.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            No pending invites.
          </div>
        ) : (
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {ws.pendingInvites.map((inv) => (
                <tr key={inv.id} className="border-b last:border-0">
                  <td className="px-4 py-3">{inv.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {ROLE_LABELS[inv.role] ?? inv.role}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(inv.expiresAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canManage ? (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`invite-resend-${inv.id}`}
                          aria-label={`Resend invite to ${inv.email}`}
                          title={`Resend invite to ${inv.email}`}
                          disabled={isResending || isRevoking}
                          onClick={() =>
                            (() => {
                              const actionId = `resend:${inv.id}`;
                              setActiveAction(actionId);
                              commitResend({
                                variables: { inviteId: inv.id },
                                onCompleted: (response, errors) => {
                                  setActiveAction(null);
                                  if (errors?.[0]) {
                                    toast.error(errors[0].message);
                                    return;
                                  }
                                  if (!response.resendWorkspaceInvite) {
                                    toast.error('Invite could not be resent');
                                    return;
                                  }
                                  toast.success(`Resent to ${inv.email}`);
                                  refresh();
                                },
                                onError: (err) => {
                                  setActiveAction(null);
                                  toast.error(err.message);
                                },
                              });
                            })()
                          }
                        >
                          <RotateCcw
                            className={`mr-2 h-4 w-4 ${activeAction === `resend:${inv.id}` ? 'animate-spin' : ''}`}
                          />
                          {activeAction === `resend:${inv.id}` ? 'Sending…' : 'Resend'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`invite-revoke-${inv.id}`}
                          aria-label={`Cancel invite to ${inv.email}`}
                          title={`Cancel invite to ${inv.email}`}
                          disabled={isRevoking || isResending}
                          onClick={() =>
                            (() => {
                              const actionId = `revoke:${inv.id}`;
                              setActiveAction(actionId);
                              commitRevoke({
                                variables: { inviteId: inv.id },
                                onCompleted: (response, errors) => {
                                  setActiveAction(null);
                                  if (errors?.[0]) {
                                    toast.error(errors[0].message);
                                    return;
                                  }
                                  if (!response.revokeWorkspaceInvite) {
                                    toast.error('Invite is no longer pending');
                                    refresh();
                                    return;
                                  }
                                  toast.success(`Cancelled invite to ${inv.email}`);
                                  refresh();
                                },
                                onError: (err) => {
                                  setActiveAction(null);
                                  toast.error(err.message);
                                },
                              });
                            })()
                          }
                        >
                          <X
                            className={`mr-1 h-4 w-4 text-foreground ${activeAction === `revoke:${inv.id}` ? 'animate-pulse' : ''}`}
                          />
                          {activeAction === `revoke:${inv.id}` ? 'Canceling…' : 'Cancel'}
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <InviteMemberDialog
        isOpen={isInviteOpen}
        onClose={onInviteClose}
        onSubmit={onInvite}
        isSubmitting={isInviting}
      />
    </>
  );
}

function InviteMemberDialog({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: InviteFormValues) => void;
  isSubmitting: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: { email: '' },
  });
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    };
  }, [isOpen, onClose, reset]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="invite-dialog"
    >
      <div
        className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-dialog-title"
        aria-describedby="invite-dialog-description"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <h2 id="invite-dialog-title" className="text-lg font-semibold">
              Invite a member
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            onClick={() => {
              reset();
              onClose();
            }}
            aria-label="Close invite dialog"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <form
          onSubmit={handleSubmit((values) => {
            onSubmit(values);
          })}
          className="space-y-4"
        >
          <div>
            <label htmlFor="invite-email" className="text-sm font-medium">
              Email address
            </label>
            <input
              id="invite-email"
              type="email"
              autoFocus
              {...register('email')}
              data-testid="invite-email"
              className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-sidebar-active"
              placeholder="teammate@company.com"
            />
            {errors.email ? (
              <p
                className="mt-1 text-xs text-destructive"
                data-testid="invite-email-error"
              >
                {errors.email.message}
              </p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="invite-submit"
            >
              {isSubmitting ? 'Sending…' : 'Send invitation'}
            </Button>
          </div>
          <p
            id="invite-dialog-description"
            className="text-xs text-muted-foreground"
          >
            The invite link is valid for 7 days. In dev (no Resend configured)
            the link is logged to the server console — copy it from there to
            test the accept flow.
          </p>
        </form>
      </div>
    </div>
  );
}
